/**
 * Admin TopBar
 * Header with restaurant selector, user menu, and logout
 */

import { LogOut, Menu, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/stores/adminAuthStore';
import { adminAuthService } from '@/services/adminAuthService';
import { useRestaurant } from '@/stores/restaurantStore';
import { AdminRoleLabels } from '@/types/admin';
import { adminRestaurantAccessService, type AdminRestaurantAccess } from '@/services/admin/adminRestaurantAccessService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { adminHomePath } from '@/utils/adminHome';

interface TopBarProps {
  onOpenMobileMenu?: () => void;
}

export function TopBar({ onOpenMobileMenu }: TopBarProps) {
  const { admin, clearAuth, setSelectedRestaurant } = useAdminAuth();
  const { currentRestaurant } = useRestaurant();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<AdminRestaurantAccess[]>([]);

  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const response = await adminRestaurantAccessService.list();
        setRestaurants(response.restaurants);

        if (response.selected_restaurant_id) {
          setSelectedRestaurant(response.selected_restaurant_id);
        }
      } catch (error) {
        console.error('Error loading admin restaurant access:', error);
      }
    };

    loadRestaurants();
  }, [setSelectedRestaurant]);

  const handleRestaurantChange = async (restaurantId: string) => {
    if (!restaurantId || restaurantId === admin?.selected_restaurant_id) return;

    try {
      await adminRestaurantAccessService.select(restaurantId);
      setSelectedRestaurant(restaurantId);
      sessionStorage.removeItem('restaurant');
      sessionStorage.removeItem('items');
      window.location.href = adminHomePath(admin?.role);
    } catch (error) {
      console.error('Error switching restaurant:', error);
    }
  };

  const handleLogout = async () => {
    await adminAuthService.logout();
    clearAuth();
    navigate('/admin/login');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex min-h-16 items-center justify-between border-b border-gray-200 bg-white px-4 py-2 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onOpenMobileMenu}
          className="h-11 w-11 md:hidden"
          aria-label="Abrir menú de navegación"
        >
          <Menu className="h-5 w-5" />
        </Button>

        <div className="min-w-0">
          {restaurants.length > 1 ? (
            <Select
              value={admin?.selected_restaurant_id || ''}
              onValueChange={handleRestaurantChange}
            >
              <SelectTrigger
                className="max-w-[240px] h-8 border-gray-300 text-[14px] font-light text-[#1e1e1e]"
                aria-label="Seleccionar restaurante"
              >
                <SelectValue placeholder="Seleccionar restaurante" />
              </SelectTrigger>
              <SelectContent>
                {restaurants.map((restaurant) => (
                  <SelectItem key={restaurant.id} value={restaurant.id}>
                    {restaurant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <h2 className="truncate text-[16px] font-light text-[#1e1e1e]">
              {currentRestaurant?.name || 'Kombu'}
            </h2>
          )}
          <p className="text-[12px] font-light text-[#9d9c9c] uppercase tracking-[0.5px]">
            Panel de Administración
          </p>
        </div>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex min-h-11 items-center gap-2 hover:bg-[#eeeeee]">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-[#1e1e1e] text-white text-xs font-light">
                {admin?.name ? getInitials(admin.name) : 'AD'}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-left">
              <p className="text-[14px] font-light text-[#1e1e1e]">{admin?.name}</p>
              <p className="text-[12px] font-light text-[#9d9c9c]">
                {admin?.role ? AdminRoleLabels[admin.role] : ''}
              </p>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 border-[#1e1e1e]">
          <DropdownMenuLabel>
            <div>
              <p className="font-light text-[#1e1e1e]">{admin?.name}</p>
              <p className="text-[12px] font-light text-[#9d9c9c]">{admin?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-gray-200" />
          <DropdownMenuItem
            onClick={() => navigate('/admin/profile')}
            className="min-h-11 font-light text-[#1e1e1e] hover:bg-[#eeeeee] cursor-pointer"
          >
            <User className="mr-2 h-4 w-4" />
            Perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-gray-200" />
          <DropdownMenuItem
            onClick={handleLogout}
            className="min-h-11 font-light text-[#1e1e1e] hover:bg-[#eeeeee] cursor-pointer"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
